import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { EYesNo } from '../../../../../shared/models/types.enum';
import { DateUtils } from '../../../../../shared/helpers/dateUtils';

@Component({
  selector: 'code-of-conduct',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './code-of-conduct.component.html',
  styleUrl: './code-of-conduct.component.css'
})
export class CodeOfConductComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('datepickerSuspensionReturnDate') datepickerSuspensionReturnDate!: ElementRef;

  @Input() rmsPlanChecklistData: any;
  @Output() onSaveSection3 = new EventEmitter<any>();

  yesNo = EYesNo;
  // yesNo = { Yes: 1, No: 0 };
  codeOfConductForm: FormGroup = new FormGroup([]);

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: object
  ) { }

  ngOnInit() {
    this.initializeForm();
    this.setupFormChangeHandlers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rmsPlanChecklistData'] && changes['rmsPlanChecklistData'].currentValue) {
      this.initializeForm();
    }
  }
  private initializeForm() {
    const group: any = {};

    group['code_of_conduct'] = new FormControl(String(this.rmsPlanChecklistData?.code_of_conduct) || this.yesNo.No, Validators.required);
    group['disciplinary_action_taken'] = new FormControl(String(this.rmsPlanChecklistData?.disciplinary_action_taken) || this.yesNo.No);
    group['action_taken'] = new FormControl(this.rmsPlanChecklistData?.action_taken || '');
    group['disciplinary_action_taken_notes'] = new FormControl(this.rmsPlanChecklistData?.disciplinary_action_taken_notes || '');
    group['suspension'] = new FormControl(String(this.rmsPlanChecklistData?.suspension) || this.yesNo.No);
    group['suspension_return_date'] = new FormControl(this.rmsPlanChecklistData?.suspension_return_date || DateUtils.formatDateForBackend(DateUtils.getCurrentFormattedDate()));
    group['action_roar_assignments'] = new FormControl(String(this.rmsPlanChecklistData?.action_roar_assignments) || this.yesNo.No);
    group['roar_assignments_planning_notes'] = new FormControl(this.rmsPlanChecklistData?.roar_assignments_planning_notes || '');

    this.codeOfConductForm = new FormGroup(group);
  }

  async ngAfterViewInit(): Promise<any> {
    if (isPlatformBrowser(this.platformId)) {
      await this.initializeDatepicker();
    }
  }

  private setupFormChangeHandlers() {
    const f = (name: string) => this.codeOfConductForm.get(name);
      // on change of code of conduct
    f('code_of_conduct')?.valueChanges.subscribe((value) => {
      if (value == this.yesNo.No) {
        this.clearAllSecondaryFields();  
      }
    });

    // on change of disciplinary action taken
    f('disciplinary_action_taken')?.valueChanges.subscribe((value) => {
      if (value === this.yesNo.No) {
        f('disciplinary_action_taken_notes')?.setValidators([Validators.required]);
        f('disciplinary_action_taken_notes')?.updateValueAndValidity();

        f('action_taken')?.setValue(null);
        f('action_taken')?.clearValidators();
        f('action_taken')?.updateValueAndValidity();

        f('suspension')?.setValue(this.yesNo.No);
        f('suspension')?.clearValidators();
        f('suspension')?.updateValueAndValidity();

        f('suspension_return_date')?.setValue(null);
        f('suspension_return_date')?.clearValidators();
        f('suspension_return_date')?.updateValueAndValidity();

        f('action_roar_assignments')?.setValue(this.yesNo.No);
        f('action_roar_assignments')?.clearValidators();
        f('action_roar_assignments')?.updateValueAndValidity();

        f('roar_assignments_planning_notes')?.setValue(null);
        f('roar_assignments_planning_notes')?.clearValidators();
        f('roar_assignments_planning_notes')?.updateValueAndValidity();
      } else {
        f('action_taken')?.setValidators([Validators.required]);
        f('action_taken')?.updateValueAndValidity();
  
        f('disciplinary_action_taken_notes')?.setValue(null);
        f('disciplinary_action_taken_notes')?.clearValidators();
        f('disciplinary_action_taken_notes')?.updateValueAndValidity();
      }
    });

    // on change of suspension
    f('suspension')?.valueChanges.subscribe(async (value) => {
      await this.initializeDatepicker();

      if (value == this.yesNo.Yes) {
        f('suspension_return_date')?.setValue(DateUtils.formatDateForBackend(DateUtils.getCurrentFormattedDate()));
        f('suspension_return_date')?.setValidators([Validators.required]);
        f('suspension_return_date')?.updateValueAndValidity();
      } else {
        f('suspension_return_date')?.setValue(null);
        f('suspension_return_date')?.clearValidators();
        f('suspension_return_date')?.updateValueAndValidity();
      }
    });

    // on change of action roar assignments
    f('action_roar_assignments')?.valueChanges.subscribe((value) => {
      if (value == this.yesNo.Yes) {
        f('roar_assignments_planning_notes')?.setValidators([Validators.required]);
        f('roar_assignments_planning_notes')?.updateValueAndValidity();
      } else {
        f('roar_assignments_planning_notes')?.clearValidators();
        f('roar_assignments_planning_notes')?.setValue(null);
        f('roar_assignments_planning_notes')?.updateValueAndValidity();
      }
      this.cdr.detectChanges();
    });
    
    f('disciplinary_action_taken')?.updateValueAndValidity();
    f('action_taken')?.updateValueAndValidity();
    f('disciplinary_action_taken_notes')?.updateValueAndValidity();
    f('suspension')?.updateValueAndValidity();
    f('suspension_return_date')?.updateValueAndValidity();
    f('action_roar_assignments')?.updateValueAndValidity();
    f('roar_assignments_planning_notes')?.updateValueAndValidity();

    this.cdr.detectChanges();
  }

  async initializeDatepicker() {
    const { Datepicker } = await import('flowbite-datepicker');
    if (this.datepickerSuspensionReturnDate) {
      new Datepicker(this.datepickerSuspensionReturnDate.nativeElement, {
        autohide: true,
        format: 'mm/dd/yyyy'
      });

      const defaultValue = this.rmsPlanChecklistData?.suspension_return_date;
      this.datepickerSuspensionReturnDate.nativeElement.value = defaultValue ? DateUtils.formatDateForDisplay(defaultValue) : DateUtils.getCurrentFormattedDate();
      
      this.datepickerSuspensionReturnDate.nativeElement.addEventListener('changeDate', (event: any) => {
        const formattedDate = DateUtils.formatDateForBackend(event.target.value);
        this.codeOfConductForm.get('suspension_return_date')?.setValue(formattedDate);
      });
    }
  }

  private clearAllSecondaryFields(): void {
    const f = (name: string) => this.codeOfConductForm.get(name);
    f('disciplinary_action_taken')?.setValue(this.yesNo.No);
    f('disciplinary_action_taken')?.clearValidators();
    f('disciplinary_action_taken')?.updateValueAndValidity();

    f('action_taken')?.setValue('');
    f('action_taken')?.clearValidators();
    f('action_taken')?.updateValueAndValidity();

    f('disciplinary_action_taken_notes')?.setValue(null);
    f('disciplinary_action_taken_notes')?.clearValidators();
    f('disciplinary_action_taken_notes')?.updateValueAndValidity();

    f('suspension')?.setValue(this.yesNo.No);
    f('suspension')?.clearValidators();
    f('suspension')?.updateValueAndValidity();

    f('suspension_return_date')?.setValue(null);
    f('suspension_return_date')?.clearValidators();
    f('suspension_return_date')?.updateValueAndValidity();

    f('action_roar_assignments')?.setValue(this.yesNo.No);
    f('action_roar_assignments')?.clearValidators();
    f('action_roar_assignments')?.updateValueAndValidity();

    f('roar_assignments_planning_notes')?.setValue(null);
    f('roar_assignments_planning_notes')?.clearValidators();
    f('roar_assignments_planning_notes')?.updateValueAndValidity();
  }

  saveCodeOfConduct(): void {
    if (this.codeOfConductForm.valid) {
      this.onSaveSection3.emit(this.codeOfConductForm.value);
    }
  }
}